package ai.jessie.web.customers

import ai.jessie.shared.customers.CustomerBillingResponse
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.actionButton
import ai.jessie.web.onboarding.busyStatus
import ai.jessie.web.onboarding.onboardingAlert
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

data class CustomerBillingUiState(
    val customers: List<CustomerBillingResponse>? = null,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
)

// A3, BR-066: read-only. Single-flight; a failed refresh never leaves earlier data on the page.
class CustomerBillingState(
    private val port: CustomerPort,
    private val scope: CoroutineScope,
    private val organisationId: String,
) {
    var state by mutableStateOf(CustomerBillingUiState())
        private set

    fun refresh() {
        if (state.busy) return
        state = state.copy(busy = true, error = null)
        scope.launch {
            try {
                val customers = port.billing(organisationId)
                currentCoroutineContext().ensureActive()
                state = state.copy(customers = customers)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: CustomerRequestFailure) {
                currentCoroutineContext().ensureActive()
                state = CustomerBillingUiState(busy = true, error = failure.customerProblem())
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                state = CustomerBillingUiState(busy = true, error = unavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }
}

// Only the finance projection is rendered: no other contact and no fallback contact detail.
@Composable
fun customerBilling(organisationId: String, state: CustomerBillingUiState, refresh: () -> Unit) {
    onboardingAlert(state.error, state.busy)
    if (state.error?.authenticationRequired != true) {
        if (state.busy) busyStatus("Wird geladen …")
        state.customers?.let { customers ->
            if (customers.isEmpty()) P { Text("Keine Kunden.") }
            customers.forEach { billingRecord(it) }
        }
        actionButton("Rechnungsangaben erneut laden", state.busy, refresh)
    }
    P { A(href = "/organisations/$organisationId") { Text("Zurück zur Organisation") } }
}

@Composable
private fun billingRecord(customer: CustomerBillingResponse) {
    val titleId = "billing-${customer.id}"
    Section(attrs = { attr("aria-labelledby", titleId) }) {
        H2(attrs = { id(titleId) }) { Text(customer.displayName) }
        if (customer.addresses.isEmpty()) P { Text("Keine Rechnungsadresse.") }
        Ul {
            customer.addresses.forEach {
                Li {
                    Text(
                        listOfNotNull(
                                it.line1,
                                it.line2,
                                "${it.postalCode} ${it.city}",
                                it.countryCode,
                            )
                            .joinToString(", ")
                    )
                }
            }
        }
        if (customer.contacts.isEmpty()) P { Text("Kein Rechnungskontakt bezeichnet.") }
        Ul {
            customer.contacts.forEach {
                Li { Text(listOfNotNull(it.name, it.email, it.phone).joinToString(", ")) }
            }
        }
    }
}
